'use client';
import React, { useCallback } from 'react';
import { useStore } from 'zustand';
import { RatioForm } from './RatioDropDown';
import { ShapeType } from '@/types/commontypes';
import type { ChartId } from '@/types/commontypes';
import { useChartShapes, useChartUIContext } from '../../contexts';
import { useChartUIStoreSelector } from '../../stores/chartUIStore';
import { globalShapeStore } from '@/stores/globalShapeStore';
import { calculateTPFromRatio, priceToNetRoe } from '@/utils/calculateSLndTP';

/**
 * TP/SL 비율 드롭다운 매니저. PopupManager 패턴으로 분리하여
 * CandlestickChart 내부 전용이던 RatioForm + handleRatioSelect 로직을
 * 외부(engview)에서도 사용할 수 있도록 한다.
 *
 * ChartShapesProvider + ChartUIProvider 하위에서 렌더링해야 한다.
 */
export const RatioManager: React.FC = () => {
  const { chartUIStore } = useChartUIContext();
  const { shapeStore } = useChartShapes();
  const ratioDropdown = useChartUIStoreSelector(chartUIStore, s => s.ratioDropdown);
  const setRatioDropdown = useChartUIStoreSelector(chartUIStore, s => s.setRatioDropdown);
  const setRenderNodes = useStore(shapeStore, s => s.setRenderNodes);

  const handleRatioSelect = useCallback(
    (uuid: string, ratio: number) => {
      const renderNodes = shapeStore.getState().renderNodes;

      // 헷지 공통 ratio박스 선택 (uuid === ''): hedge 키만 업데이트하고 헷지 페어 양쪽 TP 재계산
      if (uuid === '' && ratioDropdown.positionType === 'hedge') {
        globalShapeStore.getState().updateLastTPSLByDirection('hedge', ratio);
        setRenderNodes(prev => {
          const next = { ...prev };
          for (const [cid, chartShapes] of Object.entries(prev)) {
            const orderLines = chartShapes?.[ShapeType.ORDER_LINE] ?? [];
            const updated = orderLines.map(node => {
              const sl = node.tpslPrice?.[0]?.sl;
              if (!sl || sl === 0) return node;
              const entry = node.yValue;
              const amount = node.amount ?? 0;
              const { positionType } = node;
              const newTP = calculateTPFromRatio({ TP_SL_ratio: ratio, price: entry, amount, sl, positionType });
              return {
                ...node,
                tpslPrice: [{ tp: newTP, sl }],
                tpsl: [
                  {
                    tp: priceToNetRoe({ price: newTP, entry, amount, positionType }),
                    sl: priceToNetRoe({ price: sl, entry, amount, positionType }),
                  },
                ],
              };
            });
            next[cid as ChartId] = { ...chartShapes, [ShapeType.ORDER_LINE]: updated };
          }
          return next;
        });
        return;
      }

      for (const [cid, chartShapes] of Object.entries(renderNodes)) {
        const orderLines = chartShapes?.[ShapeType.ORDER_LINE] ?? [];
        const node = orderLines.find(n => n.uuid === uuid);
        if (!node) continue;
        const sl = node.tpslPrice?.[0]?.sl;
        if (!sl || sl === 0) continue;
        const newTP = calculateTPFromRatio({
          TP_SL_ratio: ratio,
          price: node.yValue,
          amount: node.amount ?? 0,
          sl,
          positionType: node.positionType,
        });
        globalShapeStore.getState().updateLastTPSLByDirection(node.positionType === 'short' ? 'short' : 'long', ratio);
        const entry = node.yValue;
        const amount = node.amount ?? 0;
        const { positionType } = node;
        setRenderNodes(prev => ({
          ...prev,
          [cid]: {
            ...(prev[cid as ChartId] ?? {}),
            [ShapeType.ORDER_LINE]: (prev[cid as ChartId]?.[ShapeType.ORDER_LINE] ?? []).map(n =>
              n.uuid === uuid
                ? {
                    ...n,
                    tpslPrice: [{ tp: newTP, sl }],
                    tpsl: [
                      {
                        tp: priceToNetRoe({ price: newTP, entry, amount, positionType }),
                        sl: priceToNetRoe({ price: sl, entry, amount, positionType }),
                      },
                    ],
                  }
                : n,
            ),
          },
        }));
        return;
      }
    },
    [shapeStore, setRenderNodes, ratioDropdown.positionType],
  );

  return <RatioForm dropdown={ratioDropdown} setRatioDropdown={setRatioDropdown} onSelect={handleRatioSelect} />;
};
